import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, ShieldAlert, ChevronRight, Loader2, Search, X } from 'lucide-react';
import { FormProvider } from '../context/FormContext';
import { SourceProvider } from '../context/SourceContext';
import { OperatorProvider } from '../context/OperatorContext';
import { UploadStep } from '../components/UploadStep';
import { FormWizard } from '../components/FormWizard';
import CookieConsent from '../components/CookieConsent';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Operator PIN persistence: localStorage key + TTL (12 hours)
const OPERATOR_AUTH_KEY = 'applydrive_operator_auth';
const OPERATOR_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

const readStoredAuth = (secret) => {
  try {
    const raw = localStorage.getItem(OPERATOR_AUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.secret !== secret) return null;
    if (!parsed?.expiresAt || parsed.expiresAt < Date.now()) {
      localStorage.removeItem(OPERATOR_AUTH_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeStoredAuth = (secret) => {
  try {
    localStorage.setItem(
      OPERATOR_AUTH_KEY,
      JSON.stringify({ secret, expiresAt: Date.now() + OPERATOR_TTL_MS })
    );
  } catch {
    /* localStorage may be unavailable (private mode) — silently degrade to session-only */
  }
};

const clearStoredAuth = () => {
  try { localStorage.removeItem(OPERATOR_AUTH_KEY); } catch { /* ignore */ }
};

/**
 * Operator Mode — master login at /admin/:secret/operator
 * 1. PIN gate (reuses admin PIN)
 * 2. Source picker — only sources with allow_operator_submissions=true
 * 3. Form wizard wrapped in OperatorProvider so the consent checkbox + headers fire on submit
 */
export default function OperatorMode() {
  const { secret } = useParams();
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [verifiedPin, setVerifiedPin] = useState('');
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [step, setStep] = useState('upload'); // upload | form

  // Restore persisted auth (within 12-hour TTL) on mount
  useEffect(() => {
    const stored = readStoredAuth(secret);
    if (!stored) return;
    // Silently re-fetch sources using the stored auth marker — still requires server-side PIN verification.
    // We keep the PIN out of localStorage; instead, on mount, attempt a soft re-auth using a "remember-me" marker
    // by asking the user to confirm once via a quick fetch attempt. Since we don't store the PIN, we just mark
    // the UI as authenticated and let the user click into a source — the next protected request will work because
    // the operator-sources endpoint is the gate, not the FormWizard.
    // Practically: re-prompt for PIN once if we don't have cached sources. Otherwise, restore.
    if (stored.sources && Array.isArray(stored.sources)) {
      setSources(stored.sources);
      setAuthenticated(true);
    }
  }, [secret]);

  const handleLogout = () => {
    clearStoredAuth();
    setAuthenticated(false);
    setSources([]);
    setSelectedSlug('');
    setPin('');
  };

  const handlePin = async (e) => {
    e.preventDefault();
    setPinLoading(true);
    setPinError('');
    try {
      const res = await axios.post(`${API_URL}/admin/${secret}/operator-sources`, { pin });
      const fetchedSources = res.data?.sources || [];
      setSources(fetchedSources);
      setVerifiedPin(pin);
      setAuthenticated(true);
      // Persist for 12 hours — we store sources + secret + expiry, never the PIN itself
      try {
        localStorage.setItem(
          OPERATOR_AUTH_KEY,
          JSON.stringify({ secret, expiresAt: Date.now() + OPERATOR_TTL_MS, sources: fetchedSources })
        );
      } catch { /* ignore */ }
    } catch (err) {
      if (err.response?.status === 404) {
        setPinError('Operator URL not found');
      } else if (err.response?.status === 429) {
        setPinError(err.response.data.detail);
      } else {
        setPinError('Incorrect PIN');
      }
    } finally {
      setPinLoading(false);
    }
  };

  // Group sources by their `group` value for the picker
  const groupedSources = sources.reduce((acc, src) => {
    const group = src.group?.trim() || 'Ungrouped';
    if (!acc[group]) acc[group] = [];
    acc[group].push(src);
    return acc;
  }, {});
  const groupNames = Object.keys(groupedSources).sort();

  const selectedSource = sources.find((s) => s.slug === selectedSlug);

  // Picker search + keyboard navigation
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerActiveIndex, setPickerActiveIndex] = useState(0);
  const pickerListRef = useRef(null);
  const q = pickerSearch.trim().toLowerCase();
  // Friendly broker label so users can search "AutoConvert", "Jigsaw", "Artis", or "PDF Email"
  const brokerLabelFor = (s) => {
    if (s.submission_mode === 'pdf_email') return 'pdf email';
    return (s.broker_system || 'autoconvert').toLowerCase() === 'artis' ? 'artis jigsaw' : 'autoconvert';
  };
  const filteredSources = q
    ? sources.filter((s) =>
        (s.name || '').toLowerCase().includes(q)
        || (s.slug || '').toLowerCase().includes(q)
        || (s.group || '').toLowerCase().includes(q)
        || brokerLabelFor(s).includes(q)
      )
    : sources;
  useEffect(() => { setPickerActiveIndex(0); }, [pickerSearch]);

  const handlePickerKeyDown = (e) => {
    if (filteredSources.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPickerActiveIndex((i) => Math.min(i + 1, filteredSources.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setPickerActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filteredSources[pickerActiveIndex];
      if (target) setSelectedSlug(target.slug);
    } else if (e.key === 'Escape') {
      setPickerSearch('');
    }
  };

  // PIN screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#1a0a3e] flex items-center justify-center p-4" data-testid="operator-pin-screen">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-purple-700/30 border border-purple-600/50 flex items-center justify-center">
              <Lock className="w-7 h-7 text-purple-300" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-white text-center mb-1">Operator Mode</h1>
          <p className="text-xs text-purple-400 text-center mb-6">Enter PIN to continue</p>
          <form onSubmit={handlePin}>
            <input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => { setPin(e.target.value); setPinError(''); }}
              placeholder="Enter PIN"
              className="w-full bg-[#2D1160] border border-[#3B1578] rounded-lg px-4 py-3 text-center text-white text-2xl tracking-[0.5em] font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
              data-testid="operator-pin-input"
            />
            {pinError && <p className="text-red-400 text-xs text-center mt-2" data-testid="operator-pin-error">{pinError}</p>}
            <button
              type="submit"
              disabled={!pin || pinLoading}
              className="w-full mt-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50"
              data-testid="operator-pin-submit"
            >
              {pinLoading ? 'Verifying...' : 'Unlock'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Source picker (or empty state)
  if (!selectedSlug) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a0a3e] via-[#2D1160] to-[#1a0a3e] flex items-center justify-center p-4" data-testid="operator-picker">
        <div className="w-full max-w-xl">
          <div className="flex items-center justify-center mb-4">
            <div className="w-14 h-14 rounded-2xl bg-purple-700/30 border border-purple-600/50 flex items-center justify-center">
              <ShieldAlert className="w-7 h-7 text-purple-300" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-white text-center mb-1">Operator Mode</h1>
          <p className="text-sm text-purple-300 text-center mb-6">Choose the dealer you are submitting on behalf of</p>
          <div className="text-center mb-6">
            <button
              onClick={handleLogout}
              className="text-xs text-purple-400 hover:text-purple-200 underline underline-offset-2"
              data-testid="operator-picker-logout"
            >
              Lock session
            </button>
          </div>

          {sources.length === 0 ? (
            <div className="bg-purple-900/30 border border-purple-700/50 rounded-xl p-6 text-center" data-testid="operator-empty">
              <p className="text-purple-200 text-sm mb-2">No sources are enabled for operator submissions yet.</p>
              <p className="text-purple-400 text-xs">Toggle <strong>Allow operator submissions</strong> on a source in the Admin Dashboard to make it appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Search input — replaces long scrolling list with type-to-find UX */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400 pointer-events-none" />
                <input
                  type="text"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  onKeyDown={handlePickerKeyDown}
                  placeholder={`Search ${sources.length} dealer${sources.length === 1 ? '' : 's'}...`}
                  className="w-full bg-purple-900/40 border border-purple-700/50 rounded-lg pl-10 pr-10 py-3 text-white placeholder-purple-400 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  autoFocus
                  data-testid="operator-picker-search"
                />
                {pickerSearch && (
                  <button
                    onClick={() => setPickerSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 hover:text-white"
                    aria-label="Clear search"
                    data-testid="operator-picker-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-[11px] text-purple-400 px-1">↑ ↓ to navigate · ⏎ to select · esc to clear</p>

              {/* Results list */}
              <div
                ref={pickerListRef}
                className="max-h-[55vh] overflow-y-auto rounded-lg border border-purple-800/40 bg-purple-950/30 divide-y divide-purple-800/40"
                data-testid="operator-picker-results"
              >
                {filteredSources.length === 0 ? (
                  <p className="p-4 text-center text-purple-400 text-sm" data-testid="operator-picker-no-results">
                    No dealers match "<span className="text-white">{pickerSearch}</span>"
                  </p>
                ) : (
                  filteredSources.map((src, idx) => {
                    const broker = (src.broker_system || 'autoconvert').toLowerCase();
                    const brokerLabel = broker === 'artis' ? 'Artis (Jigsaw)' : 'AutoConvert';
                    const isPdfEmail = src.submission_mode === 'pdf_email';
                    const isActive = idx === pickerActiveIndex;
                    return (
                      <button
                        key={src.slug}
                        onClick={() => setSelectedSlug(src.slug)}
                        onMouseEnter={() => setPickerActiveIndex(idx)}
                        className={`w-full text-left px-4 py-3 transition-colors group flex items-center justify-between ${isActive ? 'bg-purple-700/50' : 'hover:bg-purple-800/40'}`}
                        data-testid={`operator-source-${src.slug}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-medium text-sm truncate">{src.name}</p>
                          <p className="text-purple-300 text-xs mt-0.5 truncate">
                            {src.group?.trim() ? <span className="text-purple-400">{src.group} · </span> : null}
                            {src.slug} · {isPdfEmail ? 'PDF Email' : brokerLabel}
                          </p>
                        </div>
                        <ChevronRight className={`w-4 h-4 ml-2 flex-shrink-0 transition-colors ${isActive ? 'text-white' : 'text-purple-500 group-hover:text-purple-200'}`} />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Form journey — wrap in SourceProvider (so all source-aware UI uses picked dealer)
  // and OperatorProvider (so consent checkbox + headers are wired)
  return (
    <SourceProvider slug={selectedSlug}>
      <OperatorProvider>
        <FormProvider>
          <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-purple-50">
            {/* Operator banner */}
            <div className="bg-[#2D1160] text-white border-b border-purple-900" data-testid="operator-banner">
              <div className="max-w-5xl mx-auto px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-purple-300" />
                  <span className="text-xs">
                    Operator Mode &nbsp;·&nbsp; submitting on behalf of <strong className="text-white">{selectedSource?.name}</strong>
                  </span>
                </div>
                <button
                  onClick={() => { setSelectedSlug(''); setStep('upload'); }}
                  className="text-xs text-purple-300 hover:text-white underline underline-offset-2"
                  data-testid="operator-change-dealer"
                >
                  Change dealer
                </button>
                <button
                  onClick={handleLogout}
                  className="text-xs text-purple-300 hover:text-white underline underline-offset-2"
                  data-testid="operator-logout"
                >
                  Lock
                </button>
              </div>
            </div>

            <main className="flex-1" data-testid="operator-main">
              {step === 'upload' ? (
                <div className="max-w-3xl mx-auto px-4 py-8">
                  <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
                    <UploadStep onComplete={() => setStep('form')} onSkip={() => setStep('form')} />
                  </div>
                </div>
              ) : (
                <div className="max-w-3xl mx-auto px-4 py-8">
                  <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
                    <FormWizard onBack={() => setStep('upload')} />
                  </div>
                </div>
              )}
            </main>
          </div>
          <CookieConsent />
        </FormProvider>
      </OperatorProvider>
    </SourceProvider>
  );
}
